import { Modal } from "@mantine/core";
import IconX from "@src/assets/icons/tabler/x.svg?react";
import { ROUTES } from "@src/common/constants";
import config from "@src/config";
import { getPublicWebAppOrigin } from "@src/utils/app-url";
import {
	EmbeddedCheckout,
	EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useMemo, useState } from "react";
import { createCheckoutMutationFn } from "./mutations/create-checkout";

let stripePromise: Promise<Stripe | null> | null = null;
function getStripePromise(): Promise<Stripe | null> | null {
	if (!stripePromise && config.stripePublishableKey) {
		stripePromise = loadStripe(config.stripePublishableKey);
	}
	return stripePromise;
}

type StripeCheckoutModalProps = {
	opened: boolean;
	onClose: () => void;
	priceId?: string;
	discountCode?: string;
	fetchClientSecretOverride?: () => Promise<string>;
	zIndex?: number;
};

export function StripeCheckoutModal(props: StripeCheckoutModalProps) {
	const {
		opened,
		onClose,
		priceId,
		discountCode,
		fetchClientSecretOverride,
		zIndex = 3001,
	} = props;
	const [error, setError] = useState<string | null>(null);

	// Stripe's EmbeddedCheckoutProvider expects a function that returns the client secret
	const fetchClientSecret = useCallback(async () => {
		try {
			setError(null);
			if (fetchClientSecretOverride) {
				return await fetchClientSecretOverride();
			}

			if (!priceId) {
				throw new Error("Checkout price is missing");
			}

			const result = await createCheckoutMutationFn({
				priceId,
				discountCode,
				returnUrl: `${getPublicWebAppOrigin()}${ROUTES.checkoutReturn}?session_id={CHECKOUT_SESSION_ID}`,
			});

			if (!result?.secret) {
				throw new Error("Failed to create checkout session");
			}

			return result.secret;
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Failed to initialize checkout";
			setError(errorMessage);
			throw err;
		}
	}, [fetchClientSecretOverride, priceId, discountCode]);

	const options = useMemo(() => ({ fetchClientSecret }), [fetchClientSecret]);

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			centered
			size={520}
			padding={0}
			withCloseButton={false}
			zIndex={zIndex}
			withinPortal
			// Stripe Elements captures pointer events via an iframe; without
			// pointerEvents:"auto" the Mantine overlay blocks all clicks inside it.
			overlayProps={{
				style: { pointerEvents: "auto" },
			}}
			styles={{
				root: { pointerEvents: "auto" },
				content: { pointerEvents: "auto" },
			}}
			classNames={{
				content:
					"relative bg-surface-default rounded-2xl border border-surface-default/20 shadow-2xl",
				body: "p-0 py-10",
			}}
		>
			<button
				type="button"
				onClick={onClose}
				className="absolute top-3 right-3 z-20 h-8 w-8 rounded-full bg-surface-default/90 backdrop-blur shadow-md hover:bg-surface-default flex items-center justify-center transition-colors"
				aria-label="Close checkout"
			>
				<IconX className="w-4 h-4 text-muted" />
			</button>

			<div className="min-h-100 relative">
				{error && (
					<div className="absolute inset-0 flex items-center justify-center p-6">
						<div className="text-center">
							<p className="text-danger mb-4">{error}</p>
							<button
								type="button"
								onClick={onClose}
								className="px-4 py-2 bg-surface-subtle hover:bg-surface-hover-muted rounded-md text-sm font-medium"
								title="Close checkout"
								aria-label="Close checkout"
							>
								Close
							</button>
						</div>
					</div>
				)}

				{!error && opened && (
					<EmbeddedCheckoutProvider
						stripe={getStripePromise()}
						options={options}
					>
						<EmbeddedCheckout />
					</EmbeddedCheckoutProvider>
				)}
			</div>
		</Modal>
	);
}
